import { useState } from 'react';

import Connection, { AccessToken, SetLightsOptions } from '../Connection';
import { generatePKCEPair } from '../api/openIdUtils';
import { SpotLayoutItem } from '../api/spotLayoutItems';
import {
    AccessTokenStored,
    fetchOAuth2Token,
    getAuthorizationCodeFromUrl,
    getOauth2RedirectUri,
    getStateFromUrl,
    isAuthorizationRequestResponse,
    parseExpiresIn,
    parseResponseToAccessToken,
    refreshOAuth2Token,
} from '../common/utils';
import useLocalStorage from '../hooks/useLocalStorage';
import useSelectTerminalDriver from '../hooks/useSelectTeminalDriver';
import { Logger } from '../logs/Logger';
import { TerminalSoftware } from '../terminal/terminalSoftwareHelpers/TerminalConnectionManager';
import { getCryptoRandomString, sleep } from '../utils';
import { redirectUri } from './OAuth2';

const useDemoLib = (): Connection => {
    const app = useSelectTerminalDriver();

    const [oauth2Result, changeOauth2Result] = useLocalStorage<AccessTokenStored | null>('oauth2Result', null);
    const [oauth2State, changeOauth2State] = useLocalStorage<string | null>('oauth2_state', null);

    const [demoLib] = useState(() => {
        const connection: Connection = {
            getTerminalSoftware: function (): TerminalSoftware {
                return TerminalSoftware.UNKNOWN;
            },
            getTerminalSoftwareVersion(): number {
                return 4.0;
            },
            async init(): Promise<AccessToken | null> {
                await Logger.log('DemoLib: init');
                const urlStr = window.location.toString();
                if (isAuthorizationRequestResponse(urlStr)) {
                    if (oauth2State) {
                        await Logger.debug('This is an authorization request response.');
                        changeOauth2State(null);
                        const responseState = getStateFromUrl(urlStr);
                        if (responseState !== oauth2State) {
                            await Logger.warn('state in oauth2 response does not match expected state', {}, responseState, oauth2State);
                            return null;
                        }
                        const authorizationCode = getAuthorizationCodeFromUrl(urlStr);
                        if (!authorizationCode) {
                            await Logger.warn('No code found in otherwise valid oauth2 response', {}, urlStr);
                            return null;
                        }
                        try {
                            const result = await fetchOAuth2Token(authorizationCode);
                            await Logger.debug('Fetched an access token', {}, result);
                            const expiresIn = parseExpiresIn(result.expires_in);
                            const accessTokenStored = {
                                expires: expiresIn ? expiresIn.toISOString() : null,
                                result: result,
                            };
                            changeOauth2Result(accessTokenStored);
                            window.history.replaceState({}, '', redirectUri);
                            return parseResponseToAccessToken(accessTokenStored);
                        } catch (e) {
                            await Logger.error('Token exchange failed', {}, e);
                            return null;
                        }
                    } else {
                        await Logger.warn('This is an authorization request response. No state found. Redirecting to the regular page.');
                        window.history.replaceState({}, '', redirectUri);
                    }
                }

                if (oauth2Result && oauth2Result.result) {
                    let token = parseResponseToAccessToken(oauth2Result);
                    const now = new Date();
                    now.setMinutes(now.getMinutes() + 5); // do refresh 5 minutes before expiry
                    console.log('token', token, token.expires, now, token.expires && token.expires < now);
                    if (token.expires && token.expires < now) {
                        if (!token.refreshToken) {
                            await Logger.warn('Access token expired, but no refresh token available.');
                            return null;
                        }
                        console.log('refresh token', token.refreshToken);

                        let refreshResult;
                        try {
                            refreshResult = await refreshOAuth2Token(token.refreshToken);
                        } catch (error) {
                            await Logger.warn('Unable to refresh access token.');
                            return null;
                        }
                        const expiresIn = parseExpiresIn(refreshResult.expires_in);
                        const accessTokenStored = {
                            expires: expiresIn ? expiresIn.toISOString() : null,
                            result: refreshResult,
                        };
                        token = parseResponseToAccessToken(accessTokenStored);
                        if (token.accessToken) {
                            changeOauth2Result(accessTokenStored);
                        }
                    }
                    return token;
                }

                return null;
            },

            setConfiguration(doorOpenTimeInSeconds: any) {
                Logger.log(doorOpenTimeInSeconds);
            },
            sendSpotLayoutConfig(spotLayoutItems: SpotLayoutItem[]) {
                Logger.log('config not relevant for demo');
            },
            async reConfigureTerminal(): Promise<AccessToken | null> {
                await Logger.log('DemoLib: newAccessToken');
                const state = getCryptoRandomString();
                changeOauth2State(state);
                changeOauth2Result(null);

                window.location.href = getOauth2RedirectUri(state);
                return null;
            },
            async reset(): Promise<void> {
                Logger.log('DemoLib: Reset');
                changeOauth2State(null);
                changeOauth2Result(null);
            },

            openSlot(slotObj: any): Promise<void> {
                return new Promise((resolve, reject) => {
                    if (app) app.openSlot(JSON.stringify(slotObj));
                    Logger.log('DemoLib: SLOT opened');
                    resolve();
                });
            },
            openSlotRange(first: string, last: string): Promise<void> {
                return new Promise((resolve, reject) => {
                    sleep(3000).then(() => {
                        Logger.log('DemoLib: SLOTs opened');
                        resolve();
                    });
                }); // Add a small delay to simulate slot opening
            },
            keepAlive(): void {
                Logger.log('Keep alive send');
            },
            async resetPcb(): Promise<void> {
                Logger.log('Reset PCB (refresh)');
                window.location.href = '/';
            },
            async setLights(options: SetLightsOptions): Promise<void> {
                Logger.log('Settings lights', {}, options);
            },
            resetCache(): void {
                Logger.log('Android app cache reset.');
            },
            resetData(): void {
                Logger.log('Android app data reset.');
            },
            broadcast(data) {
                /*const websocket = new WebSocket('ws://localhost:8080/');
                websocket.send(data);*/
            },
            send(id, data) {
                /*const websocket = new WebSocket('ws://localhost:8080/');
                websocket.send(data);*/
            },
            setFan: function (on: boolean): void {
                throw new Error('Function not implemented.');
            },
            setWebURL: function (url: string): void {
                throw new Error('Function not implemented.');
            },
        };
        return connection;
    });

    return demoLib as Connection;
};

export default useDemoLib;
